import { MagnifyingGlassIcon, ShieldCheckIcon, ShoppingCartIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/Ui/Button';
import Menu from '@/components/Ui/Menu';
import { UserMenu } from '@/components/Ui/UserMenu';
import { useQuote } from '@/contexts/QuoteContext';
import auth from '@/helpers/auth';
import { get as getConfig } from '@/helpers/config';
import Logo from '@/images/logo_felice_headwear.png';

import { Badge } from './Badge';

interface PageHeaderProps {
    onSettingsChange?: () => void;
}

export const PageHeader = ({ onSettingsChange }: PageHeaderProps) => {
    const config = getConfig();
    const navigate = useNavigate();
    const location = useLocation();
    const [
        t,
    ] = useTranslation('pages');

    const [
        searchOpen,
        setSearchOpen,
    ] = useState(false);
    const [
        headerSearchQuery,
        setHeaderSearchQuery,
    ] = useState('');
    const mobileSearchInputRef = useRef<HTMLInputElement>(null);

    const getEnvInformation = () => {
        if (!config.env) {
            return;
        }

        if (config.env !== 'production') {
            return <p className='h-10 text-center py-2 text-md bg-info px-4 font-medium text-white sm:px-6 lg:px-8'><Trans components={ { strong: <strong /> } } i18nKey='You are working on a <strong>{{env}}</strong> environment configuration.' values={ { env: config.env } } /></p>;
        }
    };

    // const workspace = auth.getWorkspace();
    const { totalItems, setQuotePanelOpen } = useQuote();

    const handleHeaderSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (headerSearchQuery.trim()) {
            // setSearchQuery(headerSearchQuery.trim());
            setSearchOpen(false);
            setHeaderSearchQuery('');
            navigate(`/catalog?filters=${
                 encodeURI(JSON.stringify(
                {
                    'name_or_sku': headerSearchQuery.trim(),
                }))
            }`);
        }
    };
    const handleSearchToggle = () => {
        if (searchOpen) {
            setSearchOpen(false);
        } else {
            mobileSearchInputRef.current?.focus();

            setSearchOpen(true);
        }
    };

    return (
        <>
            {getEnvInformation()}
            <header className='sm:mx-4 border-b border-border bg-secondary/80 backdrop-blur-sm sticky top-0 z-50'>
                <div className='mx-auto px-3 sm:px-6 py-2 flex items-center justify-between'>
                    <div className='flex items-center gap-4 lg:gap-8'>
                        <NavLink title='Félice Headwear' to='/'>
                            <img
                                alt='Félice Headwear'
                                className='h-12 object-contain'
                                src={ Logo }
                            />
                        </NavLink>
                        {auth.isAdministrator() && (
                            <>
                                <span className='md:hidden'>
                                    <Badge className='border-primary/40 text-primary bg-primary/5' variant='outline'>
                                        <ShieldCheckIcon className='h-3.5 w-3.5' />
                                    </Badge>
                                </span>
                                <span className='hidden md:inline-flex'>
                                    <Badge className='border-primary/40 text-primary bg-primary/5 gap-1' variant='outline'>
                                        <ShieldCheckIcon className='h-3.5 w-3.5' />
                                        <span>{t('Administrator')}</span>
                                    </Badge>
                                </span>
                            </>
                        )}
                    </div>

                    <Menu />

                    <div className='flex items-center md:gap-4'>
                        {/* Turn off dark mode for now. */}
                        {/* <div>
                            <input checked='' className='light-switch sr-only' id='light-switch' name='light-switch' onChange={ () => {
                                return toggleMode();
                            } } type='checkbox' />
                            <label className='flex items-center justify-center cursor-pointer w-8 h-8 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600/80 rounded-full' htmlFor='light-switch'>
                                <svg className='w-4 h-4 dark:hidden' height='16' width='16' xmlns='http://www.w3.org/2000/svg'>
                                    <path className='fill-current text-slate-400' d='M7 0h2v2H7V0Zm5.88 1.637 1.414 1.415-1.415 1.413-1.414-1.414 1.415-1.414ZM14 7h2v2h-2V7Zm-1.05 7.433-1.415-1.414 1.414-1.414 1.415 1.413-1.414 1.415ZM7 14h2v2H7v-2Zm-4.02.363L1.566 12.95l1.415-1.414 1.414 1.415-1.415 1.413ZM0 7h2v2H0V7Zm3.05-5.293L4.465 3.12 3.05 4.535 1.636 3.121 3.05 1.707Z'></path>
                                    <path className='fill-current text-slate-500' d='M8 4C5.8 4 4 5.8 4 8s1.8 4 4 4 4-1.8 4-4-1.8-4-4-4Z'></path>
                                </svg>
                                <svg className='w-4 h-4 hidden dark:block' height='16' width='16' xmlns='http://www.w3.org/2000/svg'>
                                    <path className='fill-current text-slate-400' d='M6.2 2C3.2 2.8 1 5.6 1 8.9 1 12.8 4.2 16 8.1 16c3.3 0 6-2.2 6.9-5.2C9.7 12.2 4.8 7.3 6.2 2Z'></path>
                                    <path className='fill-current text-slate-500' d='M12.5 6a.625.625 0 0 1-.625-.625 1.252 1.252 0 0 0-1.25-1.25.625.625 0 1 1 0-1.25 1.252 1.252 0 0 0 1.25-1.25.625.625 0 1 1 1.25 0c.001.69.56 1.249 1.25 1.25a.625.625 0 1 1 0 1.25c-.69.001-1.249.56-1.25 1.25A.625.625 0 0 1 12.5 6Z'></path>
                                </svg>
                                <span className='sr-only'>Switch to light / dark version</span>
                            </label>
                        </div> */}

                        <button
                            aria-label={ t('Search products') }
                            className='md:hidden p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors cursor-pointer'
                            onClick={ handleSearchToggle }
                        >
                            {searchOpen ? (
                                <XMarkIcon className='h-5 w-5' />
                            ) : (
                                <MagnifyingGlassIcon className='h-5 w-5' />
                            )}
                        </button>

                        <button
                            className='flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-primary/30 rounded-full text-primary hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer'
                            onClick={ () => {
                                const isCatalog = location.pathname === '/catalog';
                                const isMobile = window.innerWidth < 1024;

                                if (isMobile) {
                                    setQuotePanelOpen(true);
                                }

                                if (!isCatalog) {
                                    navigate('/catalog');
                                }
                            } }
                            type='button'
                        >
                            <ShoppingCartIcon className='h-4 w-4' />
                            <span className='lg:hidden'>{totalItems}</span>
                            <span className='hidden lg:inline'>{t('{{totalItems}} items in quote', { totalItems })}</span>
                        </button>

                        <UserMenu
                            onSettingsSave={ onSettingsChange }
                        />
                    </div>
                </div>
                {/* Mobile: Full-width search bar below header */}
                <div
                    className={ `md:hidden overflow-hidden transition-all duration-200 ease-out ${
            searchOpen ? 'max-h-16 border-t border-border' : 'max-h-0'
          }` }
                >
                    <form className='container mx-auto px-4 py-3' onSubmit={ handleHeaderSearchSubmit }>
                        <div className='flex gap-2'>
                            <div className='relative flex-1'>
                                <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                <input
                                    className='flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-9 pr-8 h-10'
                                    data-lpignore='true'
                                    onChange={ (e) => {
                                        return setHeaderSearchQuery(e.target.value);
                                    } }
                                    placeholder={ t('Search by name or sku...') }
                                    ref={ mobileSearchInputRef }
                                    type='search'
                                    value={ headerSearchQuery }
                                />
                                {headerSearchQuery && (
                                    <button
                                        className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
                                        onClick={ () => {
                                            return setHeaderSearchQuery('');
                                        } }
                                        type='button'
                                    >
                                        <XMarkIcon className='h-4 w-4' />
                                    </button>
                                )}
                            </div>
                            <Button className='h-10 px-4' size='sm' type='submit'>
                                {t('Search')}
                            </Button>
                        </div>
                    </form>
                </div>
            </header>
        </>
    );
};
